import { useLayoutEffect, useRef } from "react";

export type PylonChatPlacement = "right" | "bottom";

const CHAT_GAP = 8;

export const usePylonChatAnchor = (
  enabled: boolean,
  placement: PylonChatPlacement,
  isCollapsed: boolean,
) => {
  const ref = useRef<HTMLButtonElement>(null);

  useLayoutEffect(() => {
    const button = ref.current;
    if (!enabled || !button) return;

    const updatePosition = () => {
      const bounds = button.getBoundingClientRect();
      const width = document.documentElement.clientWidth;
      const height = window.innerHeight;
      const left = Math.min(bounds.right + CHAT_GAP, width - CHAT_GAP);
      const bottom = Math.max(CHAT_GAP, height - bounds.bottom);
      const right =
        width < 768 ? CHAT_GAP : Math.max(CHAT_GAP, width - bounds.right);
      const top =
        width < 768
          ? CHAT_GAP
          : Math.min(bounds.bottom + CHAT_GAP, height - CHAT_GAP);

      const style = document.body.style;
      const beside = placement === "right" && width >= 768;
      style.setProperty("--pylon-chat-left", beside ? `${left}px` : "auto");
      style.setProperty("--pylon-chat-right", beside ? "auto" : `${right}px`);
      style.setProperty("--pylon-chat-top", beside ? "auto" : `${top}px`);
      style.setProperty("--pylon-chat-bottom", beside ? `${bottom}px` : "auto");
      style.setProperty(
        "--pylon-chat-max-width",
        `${Math.max(0, width - (beside ? left : right) - CHAT_GAP)}px`,
      );
      style.setProperty(
        "--pylon-chat-max-height",
        `${Math.max(0, height - (beside ? bottom : top) - CHAT_GAP)}px`,
      );
      document.body.dataset.pylonAnchored = "true";
    };

    updatePosition();
    const observer = new ResizeObserver(updatePosition);
    observer.observe(button);
    if (button.parentElement) observer.observe(button.parentElement);
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
      delete document.body.dataset.pylonAnchored;
      for (const property of [
        "left",
        "right",
        "top",
        "bottom",
        "max-width",
        "max-height",
      ]) {
        document.body.style.removeProperty(`--pylon-chat-${property}`);
      }
    };
  }, [enabled, isCollapsed, placement]);

  return ref;
};
